import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { CONVERSATIONS_PAGE_SIZE } from '../../../api/conversations.api';
import { queryKeys } from '../../../api/queryKeys';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { fetchConversationMessages } from '../conversationTransport';
import {
  CHAT_MESSAGE_DOM_PREFIX,
  CHAT_UNREAD_DIVIDER_ID,
  flattenMessages,
} from '../helpers/chatMessage';

/**
 * Lead time for older history: the fetch starts this far before the reader
 * reaches the top, so pages are already there by the time they arrive.
 */
const PRELOAD_ROOT_MARGIN = '600px';
/** How close to the end still counts as "reading the latest". */
export const PINNED_THRESHOLD_PX = 100;

/**
 * Thread history.
 *
 * Inverted relative to every other list in this app: the API returns messages
 * newest-first, so `fetchNextPage` walks *backwards* through history and the
 * IntersectionObserver sentinel lives at the TOP of the scroll container.
 *
 * The two query options are not optional. `createAppQueryClient` sets
 * `refetchOnWindowFocus: true` and a 30s `staleTime` globally, and on an
 * infinite query a focus refetch re-fetches every loaded page in sequence -
 * which fights the scroll anchoring below and can clobber optimistic rows that
 * are still in flight. The socket keeps this query fresh instead.
 */
export const useChatMessages = (conversationId: number | string | null) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);

  /** Whether the user is reading the latest messages vs. scrolled back. */
  const pinnedRef = useRef(true);

  /**
   * The row the reader is looking at, and where it sat in the content the last
   * time we measured - the whole of the anchoring below is these two numbers.
   *
   * By DOM id rather than by element: rows come and go around it on a prepend,
   * and a remembered NODE only has to be replaced or unmounted once for the
   * correction to be silently skipped. The id survives re-renders, and looking
   * it up costs one querySelector.
   *
   * Null while pinned to the bottom: down there the thing to hold is the END of
   * the conversation, not any particular message.
   */
  const anchorRef = useRef<{ id: string; top: number } | null>(null);

  const query = useInfiniteQuery({
    queryKey: queryKeys.conversations.messages(conversationId ?? 0),
    queryFn: async ({ pageParam }) => {
      const { data } = await fetchConversationMessages(conversationId!, {
        cursorId: pageParam,
        limit: CONVERSATIONS_PAGE_SIZE,
      });
      return data.data as ConversationMessageWithStatus[];
    },
    enabled: conversationId !== null,
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length === CONVERSATIONS_PAGE_SIZE
        ? lastPage[lastPage.length - 1].id
        : undefined,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage, data } = query;

  const messages = flattenMessages(data?.pages);

  /**
   * Note which row is at the top of the viewport, and where it sits.
   *
   * Recorded from the reader's own scrolling, so it is always the position
   * they last chose - and re-recorded after every correction below, so a
   * correction never gets applied twice.
   */
  const captureAnchor = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (pinnedRef.current) {
      anchorRef.current = null;
      return;
    }
    /*
     * Message rows only - which is what the id filter buys, beyond the lookup.
     * The other children of this container are the two things that cannot be
     * anchored to, and both of them sit exactly where a reader ends up when
     * they reach the top:
     *
     * - the load-older sentinel, which is the first child and STAYS the first
     *   child, so a page inserted after it never moves it, and a correction
     *   measured against it is always zero;
     * - a date divider, which is removed outright when older messages from the
     *   same day arrive above it.
     *
     * Either one leaves the prepend uncorrected, which reads as the thread
     * throwing you to the top of the page that just loaded - and then doing it
     * again, because you are still at the top and the sentinel is still in
     * view.
     */
    const rows = Array.from(el.children) as HTMLElement[];
    const row = rows.find(
      (child) =>
        child.id.startsWith(CHAT_MESSAGE_DOM_PREFIX) &&
        // Optimistic rows have no server id yet, so they all answer to the
        // same one. They are only ever at the very bottom, where there is no
        // anchor anyway.
        !child.id.endsWith('undefined') &&
        child.offsetTop + child.offsetHeight > el.scrollTop
    );
    anchorRef.current = row ? { id: row.id, top: row.offsetTop } : null;
  }, []);

  /**
   * Put that row back where the reader left it.
   *
   * Everything that moves the conversation under them moves this row with it -
   * a page of older history arriving above, a picture in it resolving to a
   * different height than was reserved, an edited message re-wrapping - so how
   * far it has travelled IS the correction, whatever caused it. Content that
   * grows BELOW the anchor leaves its offset alone and is correctly ignored.
   *
   * This replaced a rule that only knew about prepends, and only at the moment
   * one committed. Pictures in a page of older history finish loading well
   * after that commit, and each one pushed the reader further down the thread
   * with nothing to answer it.
   */
  const holdAnchor = useCallback(() => {
    const el = scrollRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor) return;
    const row = el.querySelector<HTMLElement>(`[id="${anchor.id}"]`);
    if (!row) return;
    const shift = row.offsetTop - anchor.top;
    if (!shift) return;
    el.scrollTop += shift;
    anchor.top = row.offsetTop;
  }, []);

  /*
   * A conversation opens at its newest message, so position state must not
   * leak in from the previous visit. These refs survive what the DOM does
   * not: deselecting a thread unmounts the scroll container, and the fresh
   * one starts at the top - where a stale anchor still points at whatever row
   * it happens to contain, and `holdAnchor` would dutifully drag the view to
   * it while the top sentinel, in view up there, pulls in even older pages.
   *
   * Layout phase, and above the observer effect below, so the reset lands
   * first: with `pinned` already true, the ResizeObserver's initial pass puts
   * the fresh container at the bottom before anything is painted.
   */
  useLayoutEffect(() => {
    pinnedRef.current = true;
    anchorRef.current = null;
  }, [conversationId]);

  /* Track whether the user is at the bottom, so incoming messages never yank
   * someone who is reading history. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      /*
       * Settle any movement the content has made BEFORE recording where the
       * reader now is, or the re-record swallows it.
       *
       * This is the whole reason the second page still drifted. Within one
       * frame the browser lays out, then dispatches scroll events, and only
       * then runs the resize-observer steps - so a picture that grows above
       * the viewport while the reader is still scrolling is already in the
       * layout by the time this handler runs. Re-recording the anchor here
       * would file that 280px of movement as "where the reader scrolled to",
       * and the observer, arriving later in the same frame, would find nothing
       * left to correct.
       */
      holdAnchor();
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      pinnedRef.current = distance < PINNED_THRESHOLD_PX;
      captureAnchor();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [conversationId, captureAnchor, holdAnchor]);

  /**
   * Hold the conversation still while it settles.
   *
   * A thread is not its final height when it renders: pictures without measured
   * dimensions resolve to a different size than was reserved, a video only
   * knows its shape once metadata arrives, a webfont swaps and every bubble
   * re-wraps. Each of those moves everything below it.
   *
   * Which of the two things to hold depends on where the reader is. At the end
   * of the conversation, hold the END - that is what "the newest message" means
   * while it is still growing. Anywhere else, hold the row they are reading.
   *
   * Watching the ROWS for a size change, rather than listening for `load`:
   * `load` never fires for `<video>` (that is `loadedmetadata`), it does not
   * fire at all for a font or a re-wrap, and an image already in the cache can
   * finish before a listener is even attached.
   *
   * Re-attached as the list grows so new rows are watched too - and attached in
   * the LAYOUT phase, which is the whole point of the timing. A passive effect
   * runs after the browser has painted, which leaves a window where a page of
   * older history is on screen but its pictures are not yet watched by anything:
   * they resolve to their real height inside that window, push the thread down,
   * and the correction never fires. Worse, the reader is usually still
   * scrolling, so the next scroll event records the drifted position as the new
   * baseline and the shift becomes permanent. Observing before paint closes it.
   *
   * Setting `scrollTop` resizes nothing, so neither branch can feed itself.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) el.scrollTop = el.scrollHeight;
      else holdAnchor();
    });
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [conversationId, messages.length, holdAnchor]);

  /**
   * Stay at the newest message when the keyboard opens or closes.
   *
   * The conversation is sized against the VISIBLE viewport, so a keyboard
   * appearing takes about half its height away. Nothing about the scroll
   * position changes when it does, which leaves the reader looking at the
   * middle of the thread with the last few messages hidden behind the
   * composer - and the same again in reverse when the keyboard goes.
   *
   * On the next frame, not immediately: this fires alongside the listener that
   * republishes `--app-vh`, and the element has not been re-laid-out against
   * the new height yet at the moment the event arrives.
   */
  useEffect(() => {
    const el = scrollRef.current;
    const viewport = window.visualViewport;
    if (!el || !viewport) return undefined;

    const onViewportResize = () => {
      if (!pinnedRef.current) return;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    };

    viewport.addEventListener('resize', onViewportResize);
    return () => viewport.removeEventListener('resize', onViewportResize);
  }, [conversationId]);

  /* Older pages load when the top sentinel comes into view. */
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, rootMargin: PRELOAD_ROOT_MARGIN }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, conversationId]);

  /**
   * The same hold, applied to every render.
   *
   * A page of older history is the loud case - it grows the content above the
   * viewport by hundreds of pixels at once - but an edit, a deletion or a
   * `seen` tick further up does exactly the same thing on a smaller scale. The
   * anchor covers all of them without having to tell them apart, which two
   * earlier attempts at this tried to do and got wrong: one measured the height
   * from inside the IntersectionObserver, 600px of scrolling and a round trip
   * before the page actually arrived, and the other read a growing message
   * count as a prepend when it was just somebody talking.
   *
   * Before paint, so the correction is never a visible frame. Deliberately no
   * dependency array: every commit can move the content, so every commit is
   * checked.
   *
   * See also `overflow-anchor: none` on ChatScrollArea - Chrome does a version
   * of this by itself, and its correction on top of this one is a double shift.
   * Safari has no such thing, which is why this stays.
   */
  useLayoutEffect(() => {
    holdAnchor();
  });

  /**
   * Opens the thread on the "New messages" divider instead of the bottom.
   *
   * The pill lands where the first message of a freshly opened thread sits:
   * just clear of the floating header. That clearance is the scroll area's
   * own padding-top - max(24px, the measured header) - but the header is ALSO
   * measured directly, and the larger of the two wins. The padding leans on
   * `--chat-header-height`, which is state published a commit after the
   * header mounts; on a desktop the header is long mounted by the time a
   * thread opens, but the phone's inbox->thread flow mounts it together with
   * the conversation, and positioning against the not-yet-padded area put the
   * divider under the header bar. The DOM's offsetHeight has no such lag -
   * and once the variable lands and the padding grows, the anchor hold keeps
   * the divider where it was put.
   *
   * False when the divider is not in the DOM yet: the caller renders it and
   * positions in two steps, and this is how the second one waits for the
   * first.
   */
  const scrollToUnreadMarker = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return false;
    const marker = el.querySelector<HTMLElement>(`#${CHAT_UNREAD_DIVIDER_ID}`);
    if (!marker) return false;

    const headerBar =
      el.parentElement?.parentElement?.querySelector<HTMLElement>(
        '[data-chat-header]'
      );
    const clearance = Math.max(
      parseFloat(getComputedStyle(el).paddingTop) || 0,
      headerBar?.offsetHeight ?? 0
    );
    el.scrollTop = Math.max(0, marker.offsetTop - clearance);
    /*
     * Landing mid-history must also mean "not pinned", or the next incoming
     * message would yank the reader straight past everything they came here to
     * read. Measured rather than hardcoded false: a short unread stretch lands
     * within the threshold of the end and rightly stays pinned.
     */
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedRef.current = distance < PINNED_THRESHOLD_PX;
    captureAnchor();
    return true;
  }, [captureAnchor]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    pinnedRef.current = true;
    // Down here the end is what gets held, so drop whatever row was being kept
    // still - otherwise it would pull the view back mid-animation.
    anchorRef.current = null;
  }, []);

  const isPinnedToBottom = useCallback(() => pinnedRef.current, []);

  return {
    ...query,
    messages,
    scrollRef,
    topSentinelRef,
    scrollToBottom,
    scrollToUnreadMarker,
    isPinnedToBottom,
  };
};
